import React, { useEffect, useRef, useState } from "react";
import { Phone, User, Pause } from "lucide-react";
import Image from "../../../Tags/Image";
import { useTheme } from "../../../ThemeProvider";

const AudioPlayer = ({ 
  audioUrl,
  callDetails,
  callId = 1, 
  onTimeUpdate = null, 
  seekToTime = null,
  callMetadata = null  
}) => {
  const { theme } = useTheme();
  const audioRef = useRef(null);
  const cardRef = useRef(null);
  const processedAudioUrlRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardHeight, setCardHeight] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1); 
  const [processedAudioUrl, setProcessedAudioUrl] = useState(null);

  const playbackSpeeds = [1, 1.25, 1.75, 2];

  const effectiveCallDetails = callDetails;

  // Extract credentials from URL 
  const extractCredentialsAndCleanUrl = (url) => {
    if (!url) return { cleanUrl: null, credentials: null };

    try {
      const match = url.match(/https:\/\/([^:]+):([^@]+)@(.+)/);

      if (match) {
        const [, username, password, restOfUrl] = match;
        return {
          cleanUrl: `https://${restOfUrl}`,
          credentials: btoa(`${username}:${password}`),
        };
      }
    } catch (error) {
      console.error('Error extracting credentials:', error);
    }

    return { cleanUrl: url, credentials: null };
  };

  
  useEffect(() => {
    processedAudioUrlRef.current = processedAudioUrl;
  }, [processedAudioUrl]);

  
  useEffect(() => {
    const fetchAudio = async () => {
      if (!audioUrl) {
        setProcessedAudioUrl(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { cleanUrl, credentials } = extractCredentialsAndCleanUrl(audioUrl);

        if (!cleanUrl) {
          throw new Error('Invalid audio URL');
        }

       
        if (!credentials) {
          setProcessedAudioUrl(audioUrl);
          setIsLoading(false);
          return;
        }

        // console.log('Fetching authenticated audio from URL:', cleanUrl);

        const headers = {
          Accept: 'audio/mpeg, audio/wav, audio/*',
          Authorization: `Basic ${credentials}`,
        };

        const response = await fetch(cleanUrl, {
          method: 'GET',
          headers,
        });

        if (response.status < 200 || response.status >= 300) {
          console.error(`HTTP ${response.status}: ${response.statusText}`);
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setProcessedAudioUrl(blobUrl);

        console.log('Authenticated audio loaded successfully');
      } catch (err) {
        console.error('Error fetching audio:', err);
        setError("Error loading audio file");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudio();

    
    return () => {
      if (processedAudioUrlRef.current && processedAudioUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(processedAudioUrlRef.current);
        processedAudioUrlRef.current = null;
      }
    };
  }, [audioUrl]);

  const formatDateTime = (isoString) => {
    if (!isoString) return "Date not available";
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return "Date not available";
    }
  };

  const getCallDateTime = () => {
    if (callMetadata?.start_at) {
      return formatDateTime(callMetadata.start_at);
    }
    if (callMetadata?.created_at) {
      return formatDateTime(callMetadata.created_at);
    }
    
    if (callDetails?.created_at) {
      return formatDateTime(callDetails.created_at);
    }
    if (callDetails?.start_at) {
      return formatDateTime(callDetails.start_at);
    }
    
    return "Date not available";
  };

  useEffect(() => {
    // console.log('AudioPlayer - audioUrl:', audioUrl);
    // console.log('AudioPlayer - processedAudioUrl:', processedAudioUrl);
    // console.log('AudioPlayer - callDetails.recording_url:', callDetails?.recording_url);
    // console.log('AudioPlayer - callDetails:', callDetails);
    // console.log('AudioPlayer - callMetadata:', callMetadata);
    // console.log('AudioPlayer - callMetadata duration:', callMetadata?.connected_duration_seconds);
    // console.log('AudioPlayer - getCallDateTime():', getCallDateTime());
  }, [audioUrl, processedAudioUrl, callDetails, callMetadata]);

  // Handle seeking when seekToTime prop changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && seekToTime !== null && audio.duration > 0) {
      const seekTimeSeconds = seekToTime / 1000; // Convert from milliseconds to seconds
      audio.currentTime = Math.min(seekTimeSeconds, audio.duration);
    }
  }, [seekToTime]);

  // Update card height when component mounts/updates
  useEffect(() => {
    if (cardRef.current) {
      setCardHeight(cardRef.current.offsetHeight);
    }
  }, [isLoading, error]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !processedAudioUrl) return;

    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const updateTime = () => {
      const audioCurrentTime = audio.currentTime;
      const currentTimeMs = audioCurrentTime * 1000; 
      // console.log('updateTime - currentTime:', audioCurrentTime, 'duration:', audio.duration);
      setCurrentTime(audioCurrentTime);
      if (onTimeUpdate) {
        onTimeUpdate(currentTimeMs);
      }
    };
    const updateDuration = () => {
      // console.log('updateDuration - duration:', audio.duration);
      setDuration(audio.duration);
    };
    const resetPlay = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error("Audio error:", e);
      setError("Error loading audio file");
      setIsPlaying(false);
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", resetPlay);
    audio.addEventListener("error", handleError);
    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      if (audio) {
        audio.removeEventListener("timeupdate", updateTime);
        audio.removeEventListener("loadedmetadata", updateDuration);
        audio.removeEventListener("ended", resetPlay);
        audio.removeEventListener("error", handleError);
        audio.removeEventListener("loadstart", handleLoadStart);
        audio.removeEventListener("canplay", handleCanPlay);
      }
    };
  }, [processedAudioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    // console.log('togglePlay called - audioRef:', audio, 'processedAudioUrl:', processedAudioUrl, 'isPlaying:', isPlaying);
    
    if (!audio || !processedAudioUrl) {
      // console.log('togglePlay failed - no audio element or processedAudioUrl');
      return;
    }

    if (isPlaying) {
      console.log('Pausing audio');
      audio.pause();
    } else {
      // console.log('Playing audio - currentTime:', audio.currentTime, 'duration:', audio.duration, 'readyState:', audio.readyState);
      audio.play().catch((err) => {
        console.error("Error playing audio:", err);
        setError("Error playing audio");
      });
    }
    setIsPlaying(!isPlaying);
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || audio.duration === 0 || !isFinite(audio.duration)) return;

    const newTime = Math.max(0, Math.min(audio.currentTime + seconds, audio.duration));
    
    // Ensure the new time is finite and within bounds
    if (isFinite(newTime) && newTime >= 0 && newTime <= audio.duration) {
      audio.currentTime = newTime;
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || audio.duration === 0 || !isFinite(audio.duration)) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickPercent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    
    let audioPercent;
    if (clickPercent <= 1) {
      audioPercent = 0; 
    } else if (clickPercent >= 98) {
      audioPercent = 100; 
    } else {
  
      audioPercent = ((clickPercent - 1) / 97) * 100; 
    }
    
    const newTime = (audioPercent / 100) * audio.duration;
    
    if (isFinite(newTime) && newTime >= 0 && newTime <= audio.duration) {
      audio.currentTime = newTime;
    }
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  //logs data to timeline segments
  const getTimelineSegments = () => {
    
    if (!effectiveCallDetails?.logs || !Array.isArray(effectiveCallDetails.logs)) {
      return [];
    }

    // Calculating the total duration 
    let maxEnd = 0;
    effectiveCallDetails.logs.forEach(log => {
      if (log && Array.isArray(log.timelines)) {
        log.timelines.forEach(timeline => {
          if (timeline && typeof timeline.end === 'number' && timeline.end > maxEnd) {
            maxEnd = timeline.end;
          }
        });
      }
    });

    const totalDurationMs = maxEnd;

    
    if (totalDurationMs === 0) {
      return [];
    }

    const segments = [];
   
    effectiveCallDetails.logs.forEach(log => {
      
      if (log && log.speaker && Array.isArray(log.timelines)) {
       
        log.timelines.forEach(timeline => {
        
          if (timeline && typeof timeline.start === 'number' && typeof timeline.end === 'number') {
            const startPercent = (timeline.start / totalDurationMs) * 100;
            const endPercent = (timeline.end / totalDurationMs) * 100;
           
            const width = Math.max(0, endPercent - startPercent);
            
            segments.push({
              speaker: log.speaker,
              left: startPercent,
              width: width > 0.5 ? width : 0.5, 
              start: timeline.start,
              end: timeline.end
            });
          }
        });
      }
    });
    
    return segments.sort((a, b) => a.start - b.start);
  };

  // Calculate talk time percentages
  const getTalkTimeStats = () => {
    
    if (effectiveCallDetails?.speaker_timeline_summary) {
      const speakerA = effectiveCallDetails.speaker_timeline_summary.find(s => s.speaker === "A");
      const speakerB = effectiveCallDetails.speaker_timeline_summary.find(s => s.speaker === "B");
      
      const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}m ${secs}s`;
      };
      
      return {
        A: { 
          percent: speakerA ? Math.round(speakerA.percentage_of_total) : 0, 
          time: speakerA ? formatDuration(speakerA.total_talk_time_seconds) : "0m 0s"
        },
        B: { 
          percent: speakerB ? Math.round(speakerB.percentage_of_total) : 0, 
          time: speakerB ? formatDuration(speakerB.total_talk_time_seconds) : "0m 0s"
        }
      };
    }
    
    
    if (!effectiveCallDetails?.logs || !duration) return { A: { percent: 50, time: "0m 0s" }, B: { percent: 50, time: "0m 0s" } };
    
    const totalDurationMs = duration * 1000;
    let speakerATime = 0;
    let speakerBTime = 0;
    
    effectiveCallDetails.logs.forEach(log => {
      log.timelines.forEach(timeline => {
        const speakingTime = timeline.end - timeline.start;
        if (log.speaker === "A") {
          speakerATime += speakingTime;
        } else {
          speakerBTime += speakingTime;
        }
      });
    });
    
    const totalSpeakingTime = speakerATime + speakerBTime;
    const speakerAPercent = totalSpeakingTime > 0 ? Math.round((speakerATime / totalSpeakingTime) * 100) : 0;
    const speakerBPercent = 100 - speakerAPercent;
    
    const formatDuration = (ms) => {
      const totalSeconds = Math.floor(ms / 1000);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${mins}m ${secs}s`;
    };
    
    return {
      A: { 
        percent: speakerAPercent, 
        time: formatDuration(speakerATime) 
      },
      B: { 
        percent: speakerBPercent, 
        time: formatDuration(speakerBTime) 
      }
    };
  };
  // console.log("duration", duration);

  //  progress with 1% start and 98% end
  const getConstrainedProgress = () => {
    const audioProgress = duration && duration > 0 && currentTime >= 0 && isFinite(duration) && isFinite(currentTime) 
      ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) 
      : 0;
    
    if (audioProgress <= 0) {
      return 1; 
    }
    
    
    const constrainedProgress = 1 + ((audioProgress / 100) * 97); 
    
    return Math.max(1, Math.min(98, constrainedProgress));
  };

  const progress = getConstrainedProgress();
  
  // console.log('Progress calculation:', { currentTime, duration: duration, progress, isPlaying });
  
  const timelineSegments = getTimelineSegments();
  const talkTimeStats = getTalkTimeStats();


  const handleSpeedChange = (speed) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = speed;
      setPlaybackRate(speed);
      // console.log('Playback speed changed to:', speed + 'x');
    }
  };


  const cycleSpeed = () => {
    const currentIndex = playbackSpeeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackSpeeds.length;
    const nextSpeed = playbackSpeeds[nextIndex];
    handleSpeedChange(nextSpeed);
  };

  
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && processedAudioUrl) {
      audio.playbackRate = playbackRate;
    }
  }, [processedAudioUrl, playbackRate]);

  
  if (!audioUrl) {
    return (
      <div className="bg-white dark:bg-[#22262F] p-4 border border-[#EAECF0] rounded-lg">
        <div className="flex items-center justify-center space-x-3 text-gray-500 dark:text-gray-400 py-8">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.548 0-4.944-.348-7.143-.955M15 6.654V6a3 3 0 00-3-3H9a3 3 0 00-3 3v.654M15 6.654L12 9 9 6.654" />
          </svg>
          <div className="text-center">
            <p className="font-medium">No Recording Available</p>
            <p className="text-sm">This call doesn't have an audio recording</p>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveCallDetails) {
    return (
      <div className="bg-white dark:bg-[#22262F] p-4 border border-[#EAECF0] rounded-lg">
        <div className="flex items-center justify-center space-x-3 text-gray-500 dark:text-gray-400 py-8">
          <div className="text-center">
            <p className="font-medium">No Call Details Available</p>
            <p className="text-sm">Unable to load call information</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={cardRef} className="bg-white dark:bg-[#0C111D] p-4 relative border border-[#EAECF0] dark:border-[#1F242F] rounded-lg">
      
      <audio ref={audioRef} src={processedAudioUrl} preload="metadata" />

      {/* Progress indicator (playhead) - spans entire card height */}
      <div className="absolute pointer-events-none z-30" style={{
        left: `${progress}%`,
        top: 0,
        height: '145px', 
        width: '12px', 
        transform: 'translateX(-50%)'
      }}>
      <svg width="18" height="139" viewBox="0 0 18 139" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 138.001L9 13.8005" stroke="#717680" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9 15.6387L16.3612 3.90863H1.63878L9 15.6387Z" fill="#717680"/>
      </svg>

      </div>

   
      <div className="space-y-6 relative">
        {/* Speaker A */}
        <div>
          
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-[#6941C6] dark:text-[#CECFD2]" />
              <span className="text-sm text-[#6941C6] dark:text-[#CECFD2] font-semibold">
                {callDetails?.speaker_label?.A || "Speaker A"}
              </span>
            </div>
            <div className="text-right">
              <span className="text-sm text-[#7F56D9]">
                Talk time: {talkTimeStats.A.percent}% ({talkTimeStats.A.time})
              </span>
            </div>
          </div>
        
          <div 
            className="h-[10px] bg-gray-100 dark:bg-[#13161B] border dark:border-[#22262F] rounded relative cursor-pointer overflow-hidden w-full"
            onClick={handleSeek}
          >
      
            {timelineSegments
              .filter(segment => segment.speaker === "A")
              .map((segment, index) => (
                <div
                  key={`A-${index}`}
                  className="absolute h-[10px] bg-[#7F56D9] rounded-sm"
                  style={{
                    left: `${segment.left}%`,
                    width: `${segment.width}%`
                  }}
                />
              ))}
          </div>
        </div>

        {/* Speaker B */}
        <div>
          {/* Speaker info row */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-[#BA24D5]" />
              </div>
              <span className="text-sm font-semibold text-[#BA24D5]">{callDetails?.speaker_label?.B || "Speaker B"}</span>
            </div>
            <div className="text-right">
                <span className="text-sm text-[#BA24D5]">
                Talk time: {talkTimeStats.B.percent}% ({talkTimeStats.B.time})
              </span>
            </div>
          </div>
        
          <div 
            className="h-[10px] bg-gray-100 dark:bg-[#13161B] border dark:border-[#22262F] rounded relative cursor-pointer overflow-hidden w-full"
            onClick={handleSeek}
          >
          
            {timelineSegments
              .filter(segment => segment.speaker === "B")
              .map((segment, index) => (
                <div
                  key={`B-${index}`}
                  className="absolute h-[10px] bg-[#BA24D5] rounded-sm"
                  style={{
                    left: `${segment.left}%`,
                    width: `${segment.width}%`
                  }}
                />
              ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-6 mt-6 border-t border-[#EAECF0] dark:border-[#1F242F]"></div>
      <div className="flex items-center justify-between mt-8">
        <div className="text-sm text-[#475467] dark:text-[#94969C]">{getCallDateTime()}</div>
        
        <div className="flex items-center space-x-6">
          <button 
            className="text-[#717680] hover:text-gray-600 transition-colors"
            onClick={() => skip(-10)}
            title="Rewind 10s"
            disabled={isLoading}
          >
            <Image src="/images/icons/untitledui/call-logs/speed-back.svg" alt="speed back" width={16} height={16} />
          </button>
          
          <button 
            className="w-12 h-12 text-white rounded-full flex items-center justify-center"
            onClick={togglePlay}
            disabled={isLoading || error || !processedAudioUrl}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
            ) : isPlaying ? (
              theme === "dark" ? (
                <Image src="/images/icons/untitledui/call-logs/pause-button-darkMode.svg" alt="Pause" width={36} height={36} className="no-invert" />
              ) : (
                <Image src="/images/icons/untitledui/call-logs/pause-button.svg" alt="Pause" width={36} height={36} />
              )
            ) : (
              theme === "dark" ? (
                <Image src="/images/icons/untitledui/call-logs/play-button-darkMode.svg" alt="Play" width={36} height={36} className="no-invert" />
              ) : (
                <Image src="/images/icons/untitledui/call-logs/Play-Button.svg" alt="Play" width={36} height={36} />
              )
            )}
          </button>
          
          <button 
            className="text-gray-[#717680] hover:text-gray-600 transition-colors"
            onClick={() => skip(10)}
            title="Forward 10s"
            disabled={isLoading}
          >
            <Image src="/images/icons/untitledui/call-logs/speed-forward.svg" alt="speed forward" width={16} height={16} />
          </button>
        </div>
        
        <div className="flex items-center space-x-1">
          <button 
            className="text-gray-600 hover:text-gray-800 transition-colors p-1"
            onClick={() => {
              const currentIndex = playbackSpeeds.indexOf(playbackRate);
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : playbackSpeeds.length - 1;
              handleSpeedChange(playbackSpeeds[prevIndex]);
            }}
            disabled={isLoading}
            title="Decrease speed"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          
          <button
            className="text-sm text-gray-700 hover:text-gray-900 font-medium px-2 py-1 rounded transition-colors min-w-[48px]"
            onClick={cycleSpeed}
            disabled={isLoading}
            title="Click to cycle speed"
          >
            {playbackRate}x
          </button>
          
          <button 
            className="text-gray-600 hover:text-gray-800 transition-colors p-1"
            onClick={() => {
              const currentIndex = playbackSpeeds.indexOf(playbackRate);
              const nextIndex = (currentIndex + 1) % playbackSpeeds.length;
              handleSpeedChange(playbackSpeeds[nextIndex]);
            }}
            disabled={isLoading}
            title="Increase speed"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>

    

     
      {error && (
        <div className="text-sm text-red-500 mt-4 text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;